// Lõi gọi API engine Gom Đơn (/gomdon/api/*) — module THUẦN (không import gì), test ở tests/unit/web-gomdon-client.test.ts.
// client.ts gắn nó với token / luồng làm mới của lib/api.ts.
//
// Engine không có phiên riêng: mỗi yêu cầu gửi `Authorization: Bearer <access token ShopTrack>`. Lỗi trả `{ error, code }`:
//   401 expired | no_session → làm mới token rồi thử lại ĐÚNG MỘT lần;
//   401 revoked, 403 no_access | not_enabled, 503 unavailable → ném lỗi, nơi gọi tự quyết (ẩn menu / báo lỗi).

export const GOMDON_API_BASE = '/gomdon/api'

export class GomdonError extends Error {
  constructor(
    message: string,
    /** Mã HTTP; 0 = không gọi tới được (mất mạng) */
    readonly status: number,
    /** `code` engine trả kèm lỗi xác thực / quyền (expired, no_session, revoked, no_access, not_enabled, unavailable) */
    readonly code: string | null,
    /** Thân JSON của lỗi — vd `needToken` của POST /channels/facebook/pages */
    readonly data: Record<string, unknown> | null = null,
  ) {
    super(message)
    this.name = 'GomdonError'
  }
}

/** Hỏi lại GET /me khi lần trước lỗi tạm thời */
export const ME_RETRY_MS = 5 * 60_000

/**
 * Chu kỳ hỏi lại GET /me (refetchInterval của react-query): chỉ khi đang lỗi tạm thời — mất mạng (0), 5xx, thân không phải JSON… —
 * thì 5 phút hỏi lại, để menu Gom Đơn tự hiện lại khi engine lên mà không phải tải lại trang. 401 / 403 (shop chưa mở, thiếu quyền,
 * phiên hết) và lúc không lỗi: không hỏi lại theo chu kỳ.
 */
export function meRetryInterval(error: unknown): number | false {
  if (!error) return false
  if (error instanceof GomdonError && (error.status === 401 || error.status === 403)) return false
  return ME_RETRY_MS
}

export interface GomdonRequestDeps {
  fetch: (url: string, init: RequestInit) => Promise<Response>
  /** Access token ShopTrack đang giữ trong bộ nhớ */
  getToken: () => string | null
  /** Làm mới access token (dùng chung lượt đang chạy); null = không làm mới được */
  refresh: () => Promise<string | null>
}

/** 401 có các mã này: token hết hạn / thiếu → làm mới rồi thử lại một lần */
const RENEW_CODES = new Set(['expired', 'no_session'])

const asObject = (v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null)

function codeOf(v: unknown): string | null {
  const c = asObject(v)?.code
  return typeof c === 'string' ? c : null
}

/** JSON của thân trả về; null = thân rỗng; undefined = không phải JSON (vd trang HTML khi /gomdon chưa được chuyển tới engine) */
async function parse(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => '')
  if (!text) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return undefined
  }
}

function toError(res: Response, data: unknown): GomdonError {
  const obj = asObject(data)
  const text = typeof obj?.error === 'string' && obj.error.trim() ? obj.error.trim() : null
  const message = text ?? (res.status >= 500 || !obj ? `Gom Đơn đang không phản hồi (HTTP ${res.status}), thử lại sau` : `Gom Đơn báo lỗi ${res.status}`)
  return new GomdonError(message, res.status, codeOf(data), obj)
}

export function createGomdonRequest(deps: GomdonRequestDeps) {
  async function send(method: string, path: string, body: unknown, token: string | null): Promise<Response> {
    const headers: Record<string, string> = {}
    if (token) headers.Authorization = `Bearer ${token}`
    // Blob (ảnh / tin thoại gửi đi): gửi nguyên tệp, Content-Type = loại tệp; còn lại là JSON
    const blob = typeof Blob !== 'undefined' && body instanceof Blob ? body : null
    if (body !== undefined) headers['Content-Type'] = blob ? blob.type || 'application/octet-stream' : 'application/json'
    try {
      // credentials để mặc định (same-origin): cookie gomdon_fb_oauth của POST oauth/url phải được trình duyệt lưu
      return await deps.fetch(`${GOMDON_API_BASE}${path}`, { method, headers, body: body === undefined ? undefined : (blob ?? JSON.stringify(body)) })
    } catch {
      throw new GomdonError('Không kết nối được Gom Đơn, thử lại sau', 0, null)
    }
  }

  return async function gomdonRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = deps.getToken()
    let res = await send(method, path, body, token)
    let data = await parse(res)
    if (res.status === 401 && RENEW_CODES.has(codeOf(data) ?? '')) {
      // Token trong bộ nhớ đã đổi (lượt làm mới khác vừa xong) thì dùng luôn, không xoay refresh token thêm một lần nữa
      const current = deps.getToken()
      const fresh = current && current !== token ? current : await deps.refresh()
      if (fresh) {
        res = await send(method, path, body, fresh)
        data = await parse(res)
      }
    }
    if (!res.ok) throw toError(res, data)
    if (data === undefined) throw new GomdonError('Gom Đơn trả về dữ liệu không đọc được', res.status, null)
    return data as T
  }
}
